import { _ } from "libs";

import React from "react";
import { NavLink, Link, match } from "react-router-dom";
import { History, Location } from "history";
import Avatar from "./avatar";
import { Profile, MultiSearchResult, Connected } from "../types";
import {
	goBackToOldApp,
	openMyProfilePopup,
	openPlayerPopup,
	getTimezoneByLocation,
	getSupportLink,
	logout,
} from "../actions";
import NavbarDropdown, { NavbarDropdownHeader, NavbarDropdownBody } from "./navbarDropdown";
import UnreadCount from "./chat/unreadCount";
import MultiSearchInput from "./multiSearchInput";
import { getLocalMomentInZone } from "../libs/time";
import { useRemoteConfig } from "../contexts/remoteConfigContext";

const isActiveReservationsUrl = (match: match, location: Location) => {
	return new RegExp(/^\/reservations.+/).test(location.pathname);
};

const isActivePlayersUrl = (match: match, location: Location) => {
	return new RegExp(/^\/players.+/).test(location.pathname);
};

const isActiveChampionshipUrl = (match: match, location: Location) => {
	return new RegExp(/^\/championship(\s)?/).test(location.pathname);
};

const isActiveCourtsUrl = (match: match, location: Location) => {
	return new RegExp(/^\/club|courts(\s)?/).test(location.pathname);
};

interface Props {
	authenticated: boolean;
	logout: Connected<typeof logout>;
	history: History;
	profile: Profile;
}

const Navbar = ({ authenticated, logout, history, profile }: Props) => {
	let logoPath = "/";
	let showMainMenu = true;

	const webConfig = useRemoteConfig();

	if (history.location.pathname.search("/onboarding") > -1) {
		showMainMenu = false;
	}

	if (history.location.pathname.search("/tos-review") > -1) {
		showMainMenu = false;
	}

	if (history.location.pathname.search("/alter-password") > -1) {
		showMainMenu = false;
	}

	if (!authenticated) {
		logoPath = "/login";
	}

	const onSearchItemSelect = async (suggestion: MultiSearchResult) => {
		if (suggestion.type === "club") {
			history.push(`/club/${suggestion.club.clubId}`);
			return;
		}

		if (suggestion.type === "place") {
			const { place, location } = suggestion;

			const timezone = await getTimezoneByLocation(location.lat, location.lng);

			const time = getLocalMomentInZone(timezone).add(1, "h");
			history.push("/courts/results", {
				latitude: location.lat,
				longitude: location.lng,
				city: place.description,
				timezone,
				timestamp: time.unix(),
			});
			return;
		}

		if (suggestion.type === "player") {
			openPlayerPopup(suggestion.player.userId);
			return;
		}
	};

	return (
		<header className="header">
			<nav>
				<div className="main">
					<NavLink to={logoPath} className="logo" />
					{profile && authenticated && showMainMenu ? (
						<div className="field searchField searchFieldWithIcon">
							<MultiSearchInput
								key="searchNew"
								types={["club", "place", "player"]}
								onChange={onSearchItemSelect}
								placeholder={
									profile.useNetwork
										? _("nav.search.placeholder")
										: _("nav.search.notInNetwork.placeholder")
								}
							/>
						</div>
					) : null}
				</div>
				{authenticated ? (
					<>
						<div className="menu" key="navbar">
							{showMainMenu ? (
								<>
									<NavLink exact to="/" key="dashboard">
										{_("nav.dashboard")}
									</NavLink>
									<NavLink to="/courts" key="courts" isActive={isActiveCourtsUrl}>
										{_("nav.courts")}
									</NavLink>
									<NavLink
										to="/reservations/active"
										key="reservations"
										isActive={isActiveReservationsUrl}
									>
										{_("nav.reservations")}
									</NavLink>
									<NavLink to="/championships" key="championship" isActive={isActiveChampionshipUrl}>
										{_("nav.championships")}
									</NavLink>
									<NavLink to="/players/buddies" key="players" isActive={isActivePlayersUrl}>
										{_("nav.players")}
									</NavLink>
									{/* <NavLink to="/matches" key="matches">
										{_("nav.matches")}
									</NavLink> */}
									<NavLink to="/messages" key="messages">
										{_("nav.messages")}
										<UnreadCount />
									</NavLink>
									<NavLink to="/deals" key="deals">
										{_(webConfig?.deals?.translationLabel ?? "nav.deals")}
									</NavLink>
									{/* <NavLink to="/shop" key="shop">
										Shop
									</NavLink> */}
								</>
							) : null}
							{profile && authenticated ? (
								<NavbarDropdown className="user dropdown" key="user">
									<NavbarDropdownHeader>
										<div className="dropdownArea">
											<Avatar photoUrl={profile.photoUrl} size="thumb3" />
											<div className="name">{profile.firstName}</div>
										</div>
									</NavbarDropdownHeader>
									<NavbarDropdownBody>
										{history.location.pathname.search("/onboarding") === -1 ? (
											<>
												<span onClick={openMyProfilePopup}>{_("nav.myProfile")}</span>
												<Link to="/settings/account" key="settings">
													{_("nav.settings")}
												</Link>
												<div className="separator" />
											</>
										) : null}
										<a
											href={getSupportLink(profile)}
											target="_blank"
											rel="noopener noreferrer"
											id="nav.help_and_support"
										>
											{_("nav.help_and_support")}
										</a>
										<span onClick={goBackToOldApp} id="nav_old-app">
											{_("nav.backToOldApp")}
										</span>
										<div className="separator" />
										<span onClick={() => logout()} id="logout">
											{_("nav.logout")}
										</span>
									</NavbarDropdownBody>
								</NavbarDropdown>
							) : null}
						</div>
						{showMainMenu ? (
							<NavbarDropdown className="mobileMenu dropdown" key="mobileMenu">
								<NavbarDropdownHeader>
									<div className="dropdownArea">
										<div className="icon" />
										<div className="label">{_("menu")}</div>
									</div>
								</NavbarDropdownHeader>
								<NavbarDropdownBody>
									<NavLink exact to="" key="dashboard">
										{_("nav.dashboard")}
									</NavLink>
									<NavLink to="/courts" key="courts">
										{_("nav.courts")}
									</NavLink>
									<NavLink
										to="/reservations/active"
										key="reservations"
										isActive={isActiveReservationsUrl}
									>
										{_("nav.reservations")}
									</NavLink>
									<NavLink to="/players/buddies" key="players" isActive={isActivePlayersUrl}>
										{_("nav.players")}
									</NavLink>
									<NavLink to="/messages" key="messages">
										{_("nav.messages")}
										<UnreadCount />
									</NavLink>
									<NavLink to="/championships" key="championship" isActive={isActiveChampionshipUrl}>
										{_("nav.championships")}
									</NavLink>
									<NavLink to="/deals" key="deals">
										{_(webConfig?.deals?.translationLabel ?? "nav.deals")}
									</NavLink>
									<div className="separator" />
									{history.location.pathname.search("/onboarding") === -1 ? (
										<>
											<span onClick={openMyProfilePopup}>{_("nav.myProfile")}</span>
											<Link to="/settings/account" key="settings">
												{_("nav.settings")}
											</Link>
										</>
									) : null}
									<a
										href={getSupportLink(profile)}
										target="_blank"
										rel="noopener noreferrer"
										id="nav.help_and_support"
									>
										{_("nav.help_and_support")}
									</a>
									<span onClick={goBackToOldApp} id="nav_old-app">
										{_("nav.backToOldApp")}
									</span>
									<div className="separator" />
									<span onClick={() => logout()} id="logout">
										{_("nav.logout")}
									</span>
								</NavbarDropdownBody>
							</NavbarDropdown>
						) : null}
					</>
				) : (
					<div className="menu">
						{history.location.pathname === "/login" ? (
							<NavLink to="/register">{_("nav.create_account")}</NavLink>
						) : (
							<NavLink to="/login">{_("nav.sign_in")}</NavLink>
						)}
						<a href="https://www.gotcourts.com">{_("nav.back_to_homepage")} &#8617;</a>
					</div>
				)}
			</nav>
		</header>
	);
};

export default Navbar;
